import { ENV } from '@/lib/env';
import { PublicClientApplication, AuthenticationResult, PopupRequest } from '@azure/msal-browser'

/**
 * Authentication service for handling user login, logout, and session management
 */

// Define response type from OAuth endpoint
export interface OAuthResponse {
  access_token: string;
  user_id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  picture_url?: string;
}

// Define auth provider types
export type AuthProvider = 'google' | 'microsoft';

// Cookie utilities
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

// Microsoft MSAL configuration
const msalConfig = {
  auth: {
    clientId: process.env.NEXT_PUBLIC_MICROSOFT_CLIENT_ID!,
    authority: process.env.NEXT_PUBLIC_MICROSOFT_AUTHORITY!,
    redirectUri: process.env.NEXT_PUBLIC_MICROSOFT_REDIRECT_URI!,
  },
  cache: {
    cacheLocation: "sessionStorage" as const,
    storeAuthStateInCookie: false,
  }
}

let msalInstance: PublicClientApplication | null = null

// Initialize MSAL instance
const initializeMsal = async () => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig)
    await msalInstance.initialize()
  }
  return msalInstance
}

/**
 * Authenticate with an OAuth provider (Google, Microsoft)
 * @param provider The OAuth provider (google, microsoft)
 * @param token The ID token, access token, or authorization code from the provider
 * @param tokenType Optional parameter to specify if it's a 'code' or 'token'
 * @returns Promise with the authentication result
 */
export const authenticateWithOAuth = async (
  provider: AuthProvider, 
  token: string,
  tokenType?: string // Make this optional since we won't use it for MSAL
): Promise<OAuthResponse> => {
  try {
    
    // Determine the payload based on provider
    let payload: any = {
      sso_os: 'web',
      sso_type: provider,
      workspace_code: 'APP_PUBLIC'
    }

    // For both Google and Microsoft, we now send the token directly
    payload.token = token


    const response = await fetch(`${ENV.BACKEND_API}/auth/oauth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Authentication failed: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('OAuth authentication error:', error)
    throw error
  }
}

/**
 * Initialize Google OAuth flow
 * @returns Promise that resolves with the Google auth URL
 */
export const initiateGoogleAuth = async (): Promise<string> => {
  // Use our centralized ENV configuration
  const clientId = ENV.GOOGLE_AUTH.WEB_CLIENT_ID;
  
  if (!clientId) {
    throw new Error('Google client ID is not configured');
  }
  
  // Use the configured redirect URI for all environments
  const redirectUri = ENV.GOOGLE_AUTH.REDIRECT_URI;
  
  
  // Create a secure random nonce - required for ID token requests
  const nonce = crypto.randomUUID();
  
  // Store the nonce in session storage so we can verify it when the token returns
  sessionStorage.setItem('google_auth_nonce', nonce);
  
  // Build the authorization URL with parameters
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'id_token token', // Request both ID token and access token
    scope: 'openid profile email',
    prompt: 'select_account',
    access_type: 'online',
    nonce, // Including nonce is required for ID token requests
  });
  
  // Construct the full authorization URL
  const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  
  return authUrl;
}

/**
 * Extract token from URL after Google OAuth redirect
 * @returns The extracted token or null if not found
 */
export function extractTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const hashParams = window.location.hash.substring(1);
  const params = new URLSearchParams(hashParams);

  // Prefer ID token if available
  const idToken = params.get('id_token');
  const accessToken = params.get('access_token');

  // Clean up URL
  if (idToken || accessToken) {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
  }

  return idToken || accessToken; // Use ID token if possible
}

/**
 * Store authentication tokens and user data using cookies and localStorage
 */
export function storeAuthData(authResponse: any): void {
  
  // Store the access token in a secure cookie (7 days expiration)
  setCookieMobileFriendly('access_token', authResponse.access_token, 7);
  
  // Also store in localStorage for backward compatibility and immediate access
  localStorage.setItem('access_token', authResponse.access_token);
  
  // Keep permissions in memory but don't store them in localStorage
  const userPermissions = authResponse.permissions;
  
  // Store other user data but explicitly remove permissions
  const userData = { ...authResponse };
  delete userData.access_token;  // Don't store token in two places
  delete userData.permissions;   // Don't store permissions locally
  
  
  // Store the filtered user data in both cookie and localStorage
  const userDataString = JSON.stringify(userData);
  setCookieMobileFriendly('user_data', userDataString, 7);
  localStorage.setItem('user_data', userDataString);
  
  // Store permissions in both sessionStorage AND cookies for cross-tab access
  if (userPermissions) {
    const permissionsString = JSON.stringify(userPermissions);
    
    // Store in sessionStorage for current session
    sessionStorage.setItem('user_permissions', permissionsString);
    
    // Also store in cookies for cross-tab access
    setCookieMobileFriendly('user_permissions', permissionsString, 7);
  } else {
    console.warn('No permissions found in auth response');
  }
  
  // Store the authentication provider for OneDrive integration
  const storedProvider = localStorage.getItem('auth_provider')
  if (storedProvider) {
    localStorage.setItem('auth_provider_used', storedProvider)
    setCookieMobileFriendly('auth_provider_used', storedProvider, 7)
    
    // If this is a Microsoft account, refresh OneDrive authentication to handle account switching
    if (storedProvider === 'microsoft') {
      try {
        const oneDriveService = (window as any).oneDriveServiceInstance
        if (oneDriveService && typeof oneDriveService.forceRefreshAuth === 'function') {
          oneDriveService.forceRefreshAuth().catch((error: any) => {
            console.warn('Failed to refresh OneDrive auth after account change:', error)
          })
        }
      } catch (error) {
        console.warn('Error refreshing OneDrive auth:', error)
      }
    }
  }
}

/**
 * Check if user is authenticated by checking both cookies and localStorage
 */
export function isAuthenticated(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  // Check cookie first, then fallback to localStorage
  const tokenFromCookie = getCookie('access_token');
  const tokenFromStorage = localStorage.getItem('access_token');
  
  const token = tokenFromCookie || tokenFromStorage;
  
  // If we have a token in cookie but not in localStorage, sync them
  if (tokenFromCookie && !tokenFromStorage) {
    localStorage.setItem('access_token', tokenFromCookie);
    
    // Also sync user data if available
    const userDataFromCookie = getCookie('user_data');
    if (userDataFromCookie) {
      localStorage.setItem('user_data', userDataFromCookie);
    }
  }
  
  return !!token;
}

/**
 * Get the current access token from cookie or localStorage
 */
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  
  // Check cookie first, then fallback to localStorage
  return getCookie('access_token') || localStorage.getItem('access_token');
}

/**
 * Sign out the current user
 */
export function signOut(): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  // Clear OneDrive internal state without signing out of Microsoft
  try {
    // Try to access OneDrive service directly if it's already loaded
    const oneDriveService = (window as any).oneDriveServiceInstance
    if (oneDriveService && typeof oneDriveService.clearInternalState === 'function') {
      oneDriveService.clearInternalState()
    } else {
      // Fallback: Import and clear OneDrive service state
      import('@/services/cloudStorage').then(({ oneDriveService }) => {
        if (oneDriveService.isSignedIn()) {
          oneDriveService.clearInternalState()
        }
      }).catch(() => {
        // Ignore import errors - OneDrive service might not be available
      })
    }
  } catch (error) {
    console.warn('Error during OneDrive state clearing:', error)
  }
  
  // Clear cookies
  deleteCookie('access_token');
  deleteCookie('user_data');
  deleteCookie('user_permissions');
  deleteCookie('auth_provider_used');
  
  // Clear all auth related items from local storage
  localStorage.removeItem('access_token');
  localStorage.removeItem('user_data');
  localStorage.removeItem('auth_provider_used');
  sessionStorage.removeItem('user_permissions');
  
  // Clear OneDrive-related storage items
  sessionStorage.removeItem('microsoft_auth_state');
  sessionStorage.removeItem('microsoft_code_verifier');
  
  // Remove old unused keys
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_email');
  localStorage.removeItem('user_name');
  localStorage.removeItem('user_picture');
  localStorage.removeItem('auth_provider');
  localStorage.removeItem('auth_timestamp');
}

/**
 * Redirect to login if not authenticated
 */
export function redirectIfNotAuthenticated(router: any): boolean {
  if (typeof window !== 'undefined' && !isAuthenticated()) {
    router.push('/login');
    return false;
  }
  return true;
}

// Add token refresh functionality
export async function refreshToken(): Promise<boolean> {
  try {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return false

    const response = await fetch(`${ENV.BACKEND_API}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    })

    if (response.ok) {
      const data = await response.json()
      setCookieMobileFriendly('access_token', data.access_token, 7);
      localStorage.setItem('access_token', data.access_token)
      return true
    }
    return false
  } catch {
    return false
  }
}

/**
 * Set a cookie with mobile-friendly settings
 */
function setCookieMobileFriendly(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') {
    return;
  }
  
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  // More permissive cookie settings for mobile compatibility
  const cookieString = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure=${window.location.protocol === 'https:'}`;
  
  document.cookie = cookieString;
}

// Add PKCE utility functions
function base64URLEncode(str: ArrayBuffer): string {
  const bytes = new Uint8Array(str);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array.buffer);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(digest);
}

// Function specifically for switching Microsoft accounts
export const switchMicrosoftAccount = async (): Promise<string> => {
  try {
    const msal = await initializeMsal()
    
    // First, try to sign out any existing accounts silently
    const accounts = msal.getAllAccounts()
    if (accounts && accounts.length > 0) {
      try {
        // Clear the MSAL cache
        await msal.clearCache()
      } catch (clearError) {
        console.warn('Could not clear MSAL cache:', clearError)
      }
    }
    
    const loginRequest: PopupRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      prompt: 'select_account', // Force account selection
      loginHint: undefined // Don't hint any specific account
    }

    const response: AuthenticationResult = await msal.loginPopup(loginRequest)
    
    if (response.accessToken) {
      return response.accessToken
    } else {
      throw new Error('No access token received from Microsoft')
    }
  } catch (error: any) {
    console.error('Microsoft account switch error:', error)
    
    if (error.errorCode === 'user_cancelled') {
      throw new Error('Account switch was cancelled. Please try again.')
    } else if (error.errorCode === 'consent_required') {
      throw new Error('Additional permissions required. Please grant access.')
    } else {
      throw new Error('Failed to switch Microsoft account. Please try again.')
    }
  }
}

// Replace the Microsoft auth functions with popup-based flow
export const initiateMicrosoftAuthPopup = async (): Promise<string> => {
  try {
    const msal = await initializeMsal()
    
    const loginRequest: PopupRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      prompt: 'select_account'
    }

    const response: AuthenticationResult = await msal.loginPopup(loginRequest)
    
    if (response.accessToken) {
      return response.accessToken
    } else {
      throw new Error('No access token received from Microsoft')
    }
  } catch (error: any) {
    console.error('Microsoft popup auth error:', error)
    
    if (error.errorCode === 'user_cancelled') {
      throw new Error('Sign-in was cancelled. Please try again.')
    } else if (error.errorCode === 'consent_required') {
      throw new Error('Additional permissions required. Please grant access.')
    } else {
      throw new Error('Failed to sign in with Microsoft. Please try again.')
    }
  }
}

// Keep the existing redirect-based flow as fallback
export const initiateMicrosoftAuth = async (): Promise<string> => {
  const clientId = process.env.NEXT_PUBLIC_MICROSOFT_CLIENT_ID!
  const redirectUri = process.env.NEXT_PUBLIC_MICROSOFT_REDIRECT_URI!
  const authority = process.env.NEXT_PUBLIC_MICROSOFT_AUTHORITY!
  
  
  // Generate PKCE parameters
  const codeVerifier = await generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)
  
  
  // Generate state for security
  const state = crypto.randomUUID()
  
  
  // Store PKCE verifier and state
  sessionStorage.setItem('microsoft_auth_state', state)
  sessionStorage.setItem('microsoft_code_verifier', codeVerifier)
  
  
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: 'openid profile email User.Read',
    state: state,
    prompt: 'select_account',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256'
  })
  
  const authUrl = `${authority}/oauth2/v2.0/authorize?${params.toString()}`
  
  return authUrl
}

// Update the redirect response handler
export const handleMicrosoftRedirectResponse = async (): Promise<string | null> => {
  
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const state = urlParams.get('state')
  const error = urlParams.get('error')
  const errorDescription = urlParams.get('error_description')
  
  
  if (error) {
    console.error('Microsoft auth error details:', {
      error,
      errorDescription,
      fullUrl: window.location.href
    })
    throw new Error(`Microsoft auth error: ${error} - ${errorDescription}`)
  }
  
  if (!code) {
    return null
  }
  
  // Clean up URL immediately to prevent reuse
  window.history.replaceState({}, document.title, window.location.pathname)
  
  
  // Verify state
  const storedState = sessionStorage.getItem('microsoft_auth_state')
  if (state !== storedState) {
    throw new Error('Invalid state parameter')
  }
  
  // Get the code verifier
  const codeVerifier = sessionStorage.getItem('microsoft_code_verifier')
  if (!codeVerifier) {
    throw new Error('Code verifier not found')
  }

  
  // Exchange code for token with PKCE
  try {
    const tokenResponse = await exchangeMicrosoftCodeForToken(code, codeVerifier)
    
    
    // Clean up stored values
    sessionStorage.removeItem('microsoft_auth_state')
    sessionStorage.removeItem('microsoft_code_verifier')
    
    return tokenResponse.access_token
  } catch (error) {
    console.error('Token exchange error:', error)
    throw error
  }
}

// Update the token exchange function to include PKCE
export const exchangeMicrosoftCodeForToken = async (code: string, codeVerifier: string): Promise<any> => {
  const clientId = process.env.NEXT_PUBLIC_MICROSOFT_CLIENT_ID
  const redirectUri = process.env.NEXT_PUBLIC_MICROSOFT_REDIRECT_URI
  
  if (!clientId || !redirectUri) {
    throw new Error('Microsoft OAuth configuration is missing')
  }

  const tokenUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/token'
  
  const params = new URLSearchParams({
    client_id: clientId,
    scope: 'openid profile email User.Read',
    code: code,
    redirect_uri: redirectUri,
    grant_type: 'authorization_code',
    code_verifier: codeVerifier // Include PKCE verifier
  })

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  if (!response.ok) {
    const errorData = await response.text()
    console.error('Microsoft token exchange error:', errorData)
    throw new Error('Failed to exchange authorization code for tokens')
  }

  return await response.json()
} 